import React from 'react';

/**
 * De qué marca viene un lead.
 *
 * Las marcas NO están aquí: cada clínica da de alta las suyas en
 * Configuración («Marcas de los leads») y el servidor las manda con la lista
 * (`marcas`: clave, nombre y color). Quién decide la marca de cada lead es el
 * servidor (services/marca_lead.py, UNA función); aquí solo se pinta.
 *
 * Cada marca con SU color, el que se le puso en Configuración; sin color, uno
 * de una paleta neutra por su posición, para que dos marcas no se confundan.
 * Lo que no se sabe sale como «Sin identificar», con borde discontinuo y sin
 * color de ninguna: no se reparte.
 */
export const SIN_IDENTIFICAR = 'Sin identificar';

// Colores de reserva para las marcas sin color propio. No son de ninguna
// clínica: solo tienen que distinguirse entre sí.
const PALETA = ['#2E6E5E', '#1D4ED8', '#B45309', '#BE185D', '#6D28D9', '#0F766E'];

const COLOR_RE = /^#[0-9a-fA-F]{6}$/;

/**
 * El color de una marca: el suyo, o uno de reserva que no lleve ninguna otra
 * (ni por suyo ni por reserva), en el orden de las marcas.
 */
export function colorDeMarca(marcas, clave) {
  const lista = Array.isArray(marcas) ? marcas : [];
  const propio = (m) => (COLOR_RE.test(m?.color || '') ? m.color.toUpperCase() : null);
  const usados = new Set(lista.map(propio).filter(Boolean));
  let siguiente = 0;
  for (const m of lista) {
    let color = propio(m);
    if (!color) {
      const libres = PALETA.filter((c) => !usados.has(c));
      color = libres.length ? libres[siguiente % libres.length] : PALETA[siguiente % PALETA.length];
      siguiente += libres.length ? 0 : 1;
      usados.add(color);
    }
    if (m.clave === clave) return color;
  }
  return null;
}

/** El nombre de una marca, o «Sin identificar». */
export function nombreMarca(marcas, clave) {
  const lista = Array.isArray(marcas) ? marcas : [];
  return lista.find((m) => m.clave === clave)?.nombre || SIN_IDENTIFICAR;
}

/**
 * Los estilos de la etiqueta y del punto a partir del color: fondo suave,
 * borde a medias y texto del color. Van en `style` porque el color viene de
 * la base, no de una clase fija.
 */
export function estiloMarca(color) {
  if (!color) return { chip: {}, punto: {} };
  return {
    chip: { backgroundColor: `${color}1A`, color, borderColor: `${color}80` },
    punto: { backgroundColor: color },
  };
}

/**
 * La etiqueta. Con `onClick` es un botón (abre la ficha del lead); sin él,
 * una etiqueta quieta. `inline-flex` a propósito: así el tachado de la fila de
 * un contactado no la atraviesa y la marca se sigue leyendo. Una marca que ya
 * no está dada de alta se pinta como «Sin identificar»: es lo que cuenta el
 * servidor.
 */
export default function MarcaLead({
  marca, marcas, onClick, testid, title, className = '',
}) {
  const color = marca ? colorDeMarca(marcas, marca) : null;
  const estilo = estiloMarca(color);
  const sin = !color;
  const clase = `inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2 py-0.5
    text-xs font-medium no-underline ${sin ? 'bg-transparent text-muted-foreground border-dashed border-gray-400' : ''}
    ${onClick ? 'hover:opacity-80 cursor-pointer' : ''} ${className}`;
  const contenido = (
    <>
      <span aria-hidden="true" className={`w-1.5 h-1.5 rounded-full ${sin ? 'bg-gray-300' : ''}`}
        style={estilo.punto} />
      {sin ? SIN_IDENTIFICAR : nombreMarca(marcas, marca)}
    </>
  );
  const datos = { 'data-testid': testid, 'data-marca': sin ? 'sin' : marca, title };
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={clase} style={estilo.chip} {...datos}>
        {contenido}
      </button>
    );
  }
  return (
    <span className={clase} style={estilo.chip} {...datos}>
      {contenido}
    </span>
  );
}
